import { useShowNotification } from '@elotech/components';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FormularioDinamicoService } from '../../pages/formularios-dinamicos/types';
import {
  DynamicField,
  FormularioDinamicoRuntime,
  FormulariosDinamicosRuntime,
  StatusInstancia
} from '../../type/FormularioDinamico';
import { filtrarVisiveis } from '../../utils/FormularioCondicaoUtils';
import FormularioDinamicoCard from './FormularioDinamicoCard';
import {
  aplicarPadroes,
  mesmoConteudo,
  paraPerguntaInline
} from './formularioValores';
import { situacaoDe } from './situacao';
import { ArquivoHandlers, useArquivoHandlers } from './useArquivoHandlers';

type Valores = Record<string, Record<string, unknown>>;

const PREFIXO_STEP = 'fd-';

const ATRASO_EMISSAO_FORMULARIO = 150;

const aguardarEmissoesPendentes = () =>
  new Promise<void>(resolve => setTimeout(resolve, ATRASO_EMISSAO_FORMULARIO));

export const stepIdFormularioDinamico = (instanciaId: string) =>
  `${PREFIXO_STEP}${instanciaId}`;

export const isStepFormularioDinamico = (stepId?: string) =>
  !!stepId && stepId.startsWith(PREFIXO_STEP);

export const instanciaDoStep = (stepId: string) =>
  stepId.substring(PREFIXO_STEP.length);

export interface StepFormularioDinamico {
  instanciaId: string;
  stepId: string;
  titulo: string;
  icone?: string;
  visivel: boolean;
  erro?: string;
}

export interface PendenciaFormularioDinamico {
  instanciaId: string;
  mensagem: string;
}

interface Opcoes {
  service: FormularioDinamicoService;
  tipo: string;
  nomeUsuarioLogado?: string;
}

interface StepProps {
  formulario: FormularioDinamicoRuntime;
  valoresIniciais: Record<string, unknown>;
  valores: Valores;
  persistidos: Valores;
  invalido: boolean;
  arquivo?: ArquivoHandlers;
  perguntasCustomizadas: DynamicField[];
  editandoPerguntas: boolean;
  salvandoPerguntas: boolean;
  temRascunhoPerguntas: boolean;
  onValueChanges: (instanciaId: string, detail: any) => void;
  onStatusChanges: (instanciaId: string, valida: boolean) => void;
  onAlternarEdicaoPerguntas: (instanciaId: string) => void;
  onRascunhoPerguntasChange: (fields: DynamicField[]) => void;
  onConfirmarPerguntas: (instanciaId: string) => void;
}

const FormularioDinamicoStep: React.FC<StepProps> = ({
  formulario,
  valoresIniciais,
  valores,
  persistidos,
  invalido,
  arquivo,
  perguntasCustomizadas,
  editandoPerguntas,
  salvandoPerguntas,
  temRascunhoPerguntas,
  onValueChanges,
  onStatusChanges,
  onAlternarEdicaoPerguntas,
  onRascunhoPerguntasChange,
  onConfirmarPerguntas
}) => {
  const [aplicados] = useState(valoresIniciais);

  const alterarValores = useCallback(
    (detail: any) => onValueChanges(formulario.id, detail),
    [formulario.id, onValueChanges]
  );

  const alterarStatus = useCallback(
    (valida: boolean) => onStatusChanges(formulario.id, valida),
    [formulario.id, onStatusChanges]
  );

  return (
    <FormularioDinamicoCard
      formulario={formulario}
      situacao={situacaoDe(formulario, valores, persistidos, invalido)}
      values={valores[formulario.id] ?? formulario.values}
      valuesAplicados={aplicados}
      arquivo={arquivo}
      perguntasCustomizadas={perguntasCustomizadas}
      editandoPerguntas={editandoPerguntas}
      salvandoPerguntas={salvandoPerguntas}
      temRascunhoPerguntas={temRascunhoPerguntas}
      permiteEditarPerguntas
      onValueChanges={alterarValores}
      onStatusChanges={alterarStatus}
      onAlternarEdicaoPerguntas={() => onAlternarEdicaoPerguntas(formulario.id)}
      onRascunhoPerguntasChange={onRascunhoPerguntasChange}
      onConfirmarPerguntas={() => onConfirmarPerguntas(formulario.id)}
    />
  );
};

const ordenar = (formularios: FormularioDinamicoRuntime[]) =>
  [...formularios].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

export const useFormulariosDinamicosWizard = ({
  service,
  tipo,
  nomeUsuarioLogado
}: Opcoes) => {
  const showNotification = useShowNotification();

  const [runtime, setRuntime] = useState<FormulariosDinamicosRuntime>();

  const [valores, setValores] = useState<Valores>({});

  const [persistidos, setPersistidos] = useState<Valores>({});

  const [valido, setValido] = useState<Record<string, boolean>>({});

  const [erros, setErros] = useState<Record<string, string | undefined>>({});

  const [versao, setVersao] = useState(0);

  const [editandoPerguntas, setEditandoPerguntas] = useState<string>();

  const [rascunhoPerguntas, setRascunhoPerguntas] = useState<DynamicField[]>();

  const [salvandoPerguntas, setSalvandoPerguntas] = useState(false);

  const referenciaAtual = useRef<string>();

  const estado = useRef({ valores, persistidos });

  useEffect(() => {
    estado.current = { valores, persistidos };
  }, [valores, persistidos]);

  const formularios = useMemo(
    () => ordenar(runtime?.formularios ?? []),
    [runtime]
  );

  const carregar = useCallback(
    (referenciaId: string) => {
      referenciaAtual.current = referenciaId;

      return service.carregar(tipo, referenciaId).then(({ data }) => {
        const anteriores = estado.current;

        const valoresServidor = (data.formularios ?? []).reduce<Valores>(
          (acumulado, formulario) => ({
            ...acumulado,
            [formulario.id]: formulario.values || {}
          }),
          {}
        );

        const mesclados = Object.keys(valoresServidor).reduce<Valores>(
          (acumulado, instanciaId) => {
            const local = anteriores.valores[instanciaId];
            const alteradoLocalmente =
              local !== undefined &&
              !mesmoConteudo(local, anteriores.persistidos[instanciaId] ?? {});

            return {
              ...acumulado,
              [instanciaId]: alteradoLocalmente
                ? local
                : valoresServidor[instanciaId]
            };
          },
          {}
        );

        setRuntime(data);
        setPersistidos(valoresServidor);
        setValores(mesclados);
        setVersao(atual => atual + 1);
      });
    },
    [service, tipo]
  );

  const valoresDe = useCallback(
    (formulario: FormularioDinamicoRuntime) =>
      valores[formulario.id] ?? formulario.values ?? {},
    [valores]
  );

  const visiveis = useMemo(() => {
    const ids = filtrarVisiveis(
      formularios.map(formulario => ({
        ...formulario,
        values: valoresDe(formulario)
      })),
      runtime?.contexto ?? {}
    ).map(formulario => formulario.id);

    return new Set(ids);
  }, [formularios, valoresDe, runtime]);

  const semResposta = useCallback(
    (formulario: FormularioDinamicoRuntime) => {
      const atual = valoresDe(formulario);

      return (
        mesmoConteudo(atual, {}) ||
        mesmoConteudo(
          atual,
          aplicarPadroes(formulario.fields, {}, nomeUsuarioLogado)
        )
      );
    },
    [valoresDe, nomeUsuarioLogado]
  );

  const pendenciaDe = useCallback(
    (formulario: FormularioDinamicoRuntime): string | undefined => {
      if (semResposta(formulario)) {
        return formulario.obrigatorio
          ? `O formulário ${formulario.titulo} é obrigatório.`
          : undefined;
      }

      return valido[formulario.id] === false
        ? `Existem campos obrigatórios não preenchidos ou inválidos no formulário ${formulario.titulo}.`
        : undefined;
    },
    [semResposta, valido]
  );

  const salvar = useCallback(
    (instanciaId: string): Promise<void> => {
      const formulario = formularios.find(item => item.id === instanciaId);

      if (!formulario || !visiveis.has(instanciaId)) {
        return Promise.resolve();
      }

      setErros(atual => ({ ...atual, [instanciaId]: pendenciaDe(formulario) }));

      const vazio = semResposta(formulario);
      const gravado = persistidos[instanciaId] ?? {};
      const values = vazio ? {} : valoresDe(formulario);

      if (mesmoConteudo(values, gravado)) {
        return Promise.resolve();
      }

      if (!vazio && valido[instanciaId] === false) {
        return Promise.resolve();
      }

      return service.salvarRespostas(instanciaId, values).then(() => {
        setPersistidos(atual => ({ ...atual, [instanciaId]: values }));
        setValores(atual => ({ ...atual, [instanciaId]: values }));
        setRuntime(atual =>
          atual
            ? {
                ...atual,
                formularios: atual.formularios.map(item =>
                  item.id === instanciaId
                    ? {
                        ...item,
                        values,
                        status: (vazio
                          ? 'PENDENTE'
                          : 'PREENCHIDO') as StatusInstancia
                      }
                    : item
                )
              }
            : atual
        );
      });
    },
    [
      formularios,
      visiveis,
      pendenciaDe,
      semResposta,
      persistidos,
      valoresDe,
      valido,
      service
    ]
  );

  const validarFinalizacao = useCallback((): PendenciaFormularioDinamico[] => {
    const pendencias = formularios
      .filter(formulario => visiveis.has(formulario.id))
      .map(formulario => ({
        instanciaId: formulario.id,
        mensagem: pendenciaDe(formulario)
      }))
      .filter(
        (pendencia): pendencia is PendenciaFormularioDinamico =>
          !!pendencia.mensagem
      );

    setErros(
      pendencias.reduce<Record<string, string>>(
        (acumulado, pendencia) => ({
          ...acumulado,
          [pendencia.instanciaId]: pendencia.mensagem
        }),
        {}
      )
    );

    return pendencias;
  }, [formularios, visiveis, pendenciaDe]);

  const marcarPendenciasAnteriores = useCallback(
    (stepIdDestino: string) => {
      if (!isStepFormularioDinamico(stepIdDestino)) {
        return;
      }

      const indiceDestino = formularios.findIndex(
        formulario => formulario.id === instanciaDoStep(stepIdDestino)
      );

      const anteriores = formularios
        .slice(0, Math.max(indiceDestino, 0))
        .filter(formulario => visiveis.has(formulario.id));

      if (anteriores.length === 0) {
        return;
      }

      setErros(atual =>
        anteriores.reduce(
          (acumulado, formulario) => ({
            ...acumulado,
            [formulario.id]: pendenciaDe(formulario)
          }),
          atual
        )
      );
    },
    [formularios, visiveis, pendenciaDe]
  );

  const acoesAtuais = useRef({
    salvar,
    validarFinalizacao,
    marcarPendenciasAnteriores
  });

  acoesAtuais.current = {
    salvar,
    validarFinalizacao,
    marcarPendenciasAnteriores
  };

  const salvarAposEmissoes = useCallback(
    (instanciaId: string) =>
      aguardarEmissoesPendentes().then(() =>
        acoesAtuais.current.salvar(instanciaId)
      ),
    []
  );

  const validarFinalizacaoAtual = useCallback(
    () => acoesAtuais.current.validarFinalizacao(),
    []
  );

  const marcarPendenciasAnterioresAtual = useCallback(
    (stepIdDestino: string) =>
      acoesAtuais.current.marcarPendenciasAnteriores(stepIdDestino),
    []
  );

  const onValueChanges = useCallback(
    (instanciaId: string, detail: any) =>
      setValores(atual => ({ ...atual, [instanciaId]: detail })),
    []
  );

  const onStatusChanges = useCallback(
    (instanciaId: string, valida: boolean) =>
      setValido(atual => ({ ...atual, [instanciaId]: valida })),
    []
  );

  const enviarArquivo = useCallback(
    (instanciaId: string, _fileObject: any, arquivo: File) =>
      service
        .enviarArquivo(instanciaId, arquivo)
        .then(({ data }) => String(data.id)),
    [service]
  );

  const baixarArquivo = useCallback(
    (instanciaId: string, fileObject: any) =>
      service
        .urlArquivo(instanciaId, Number(fileObject?.key), false)
        .then(({ data }) => {
          window.open(data.urlAssinada, '_blank');
          return true;
        })
        .catch(() => {
          showNotification({
            level: 'error',
            message: 'Não foi possível baixar o arquivo.'
          });
          return false;
        }),
    [service, showNotification]
  );

  const arquivoDe = useArquivoHandlers(
    formularios,
    enviarArquivo,
    baixarArquivo
  );

  const perguntasCustomizadas = useMemo(
    () =>
      formularios.reduce<Record<string, DynamicField[]>>(
        (acumulado, formulario) => ({
          ...acumulado,
          [formulario.id]: (formulario.fields ?? []).filter(
            campo => campo?.origem === 'INLINE'
          )
        }),
        {}
      ),
    [formularios]
  );

  const alternarEdicaoPerguntas = useCallback((instanciaId: string) => {
    setRascunhoPerguntas(undefined);
    setEditandoPerguntas(atual =>
      atual === instanciaId ? undefined : instanciaId
    );
  }, []);

  const confirmarPerguntas = (instanciaId: string) => {
    if (!rascunhoPerguntas || !referenciaAtual.current) {
      return;
    }

    const perguntas = rascunhoPerguntas
      .filter(campo => !!campo?.label && !!campo?.type)
      .map(paraPerguntaInline);

    setSalvandoPerguntas(true);

    service
      .sincronizarPerguntasInline(instanciaId, perguntas)
      .then(() => {
        setRascunhoPerguntas(undefined);
        setEditandoPerguntas(undefined);
        showNotification({
          level: 'success',
          message: 'Perguntas customizadas salvas com sucesso.'
        });
        return carregar(referenciaAtual.current!);
      })
      .catch(error =>
        showNotification({
          level: 'error',
          message:
            error?.response?.data?.message ||
            'Não foi possível salvar as perguntas customizadas.'
        })
      )
      .finally(() => setSalvandoPerguntas(false));
  };

  const steps = useMemo<StepFormularioDinamico[]>(
    () =>
      formularios.map(formulario => ({
        instanciaId: formulario.id,
        stepId: stepIdFormularioDinamico(formulario.id),
        titulo: formulario.titulo,
        icone: formulario.icone,
        visivel: visiveis.has(formulario.id),
        erro: erros[formulario.id]
      })),
    [formularios, visiveis, erros]
  );

  const chave = useMemo(
    () => formularios.map(formulario => formulario.id).join('|'),
    [formularios]
  );

  const renderizar = (instanciaId: string) => {
    const formulario = formularios.find(item => item.id === instanciaId);

    if (!formulario) {
      return <></>;
    }

    return (
      <FormularioDinamicoStep
        key={`${formulario.id}-${versao}`}
        formulario={formulario}
        valoresIniciais={aplicarPadroes(
          formulario.fields,
          valoresDe(formulario),
          nomeUsuarioLogado
        )}
        valores={valores}
        persistidos={persistidos}
        invalido={valido[formulario.id] === false}
        arquivo={arquivoDe[formulario.id]}
        perguntasCustomizadas={perguntasCustomizadas[formulario.id]}
        editandoPerguntas={editandoPerguntas === formulario.id}
        salvandoPerguntas={salvandoPerguntas}
        temRascunhoPerguntas={!!rascunhoPerguntas}
        onValueChanges={onValueChanges}
        onStatusChanges={onStatusChanges}
        onAlternarEdicaoPerguntas={alternarEdicaoPerguntas}
        onRascunhoPerguntasChange={setRascunhoPerguntas}
        onConfirmarPerguntas={confirmarPerguntas}
      />
    );
  };

  return {
    steps,
    chave,
    carregar,
    salvar: salvarAposEmissoes,
    validarFinalizacao: validarFinalizacaoAtual,
    marcarPendenciasAnteriores: marcarPendenciasAnterioresAtual,
    renderizar
  };
};
